import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "react-toastify";

import { components } from "@/@types/api";
import { INSIGHTS_BACKEND_URL } from "@/lib/axios";
import { MutationConfig } from "@/lib/react-query";
import { axiosApi } from "@/Utils";

import { useScenarioBuilderClient } from "../../api/hooks/useScenarioBuilderClient";
import { submitScenarioV2 } from "./v2/submitScenario";

type RunScenarioRequest = components["schemas"]["RunScenarioRequestV2"];

type RunScenarioParams = {
  scenarioId: string;
  runScenarioRequest?: RunScenarioRequest;
};

export const runScenario = ({
  scenarioId,
  runScenarioRequest,
}: RunScenarioParams): Promise<any> => {
  return axiosApi.post(`/api/v2/scenarios-new/${scenarioId}/v2/run`, runScenarioRequest, {
    baseURL: INSIGHTS_BACKEND_URL,
  });
};

type UseRunScenarioOptions = {
  config?: MutationConfig<typeof runScenario | typeof submitScenarioV2>;
  isScenariosApiEnabled?: boolean;
};

export const useRunScenario = ({
  config,
  isScenariosApiEnabled = false,
}: UseRunScenarioOptions = {}) => {
  const scenarioBuilderClient = useScenarioBuilderClient();

  const runScenarioMutation = useMemo(() => {
    if (isScenariosApiEnabled) {
      return ({ scenarioId }: RunScenarioParams) =>
        submitScenarioV2({ scenarioBuilderClient, scenarioId });
    }
    return ({ scenarioId, runScenarioRequest }: RunScenarioParams) =>
      runScenario({ scenarioId, runScenarioRequest });
  }, [isScenariosApiEnabled, scenarioBuilderClient]);

  const queryClient = useQueryClient();
  return useMutation({
    onError: (error: any) => {
      console.error("useRunScenario onError", error);
      toast.error("Scenario failed to simulate. Please resubmit scenario again.");
    },
    onSuccess: async (updatedScenario) => {
      if (isScenariosApiEnabled) {
        await queryClient.invalidateQueries({ queryKey: ["scenarios-v2"] });
        await queryClient.invalidateQueries({ queryKey: ["scenario-v2", updatedScenario.uuid] });
        await queryClient.invalidateQueries({
          queryKey: ["scenario-results-v2", updatedScenario.uuid],
        });
        await queryClient.invalidateQueries({
          queryKey: ["activity-log-v2", updatedScenario.uuid],
        });
      } else {
        await queryClient.invalidateQueries({ queryKey: ["scenarios-new"] });
        await queryClient.invalidateQueries({ queryKey: ["scenario", updatedScenario.uuid] });
        await queryClient.invalidateQueries({ queryKey: ["activity-logs", updatedScenario.uuid] });
        await queryClient.invalidateQueries({ queryKey: ["is-optimizer-running"] });
      }
    },
    ...config,
    mutationFn: runScenarioMutation,
  });
};
